import {
  Alert,
  Button,
  Group,
  Loader,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";

import { useOrgScopedQueryKey } from "../../query/scope";
import {
  CiLogDisconnectModal,
  CiLogFormFields,
  CiLogLiveStatus,
  CiLogMessages,
  CiLogSummary,
} from "./CiLogsParts";
import { describeCiLogError } from "./ciLogErrors";
import {
  deleteCiLogConnection,
  putCiLogConnection,
  type CiLogConnection,
  type CiLogConnectionBody,
  type CiLogVerifyResult,
} from "./ciLogsApi";
import { useCiLogVerify } from "./useCiLogVerify";
import { useForgejoStatus } from "./useForgejoStatus";
import {
  CI_LOG_CONNECTION_KEY,
  CI_LOG_STATUS_KEY,
  useCiLogConnection,
  useCiLogProviders,
  useCiLogStatus,
} from "./useCiLogs";

// Секция «CI-логи» на `/settings/connections`.
//
// Отдельное подключение: токен вводится отдельно от репо-хоста и
// трекера. «Проверить» (`POST /verify`) ничего не пишет; «Подключить»
// сразу шлёт `PUT /` (проба + сохранение), без модалки.

const DISCONNECTED: CiLogConnection = {
  connected: false,
  provider: null,
  host_url: null,
  principal_login: null,
};

export function CiLogsSection(): ReactElement {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const providers = useCiLogProviders();
  const connection = useCiLogConnection();
  const forgejoStatus = useForgejoStatus();

  const isConnected = connection.data?.connected === true;
  const status = useCiLogStatus(isConnected);

  // Ключ кэша подключения текущей организации; `!` безопасен: кнопки
  // мутаций рисуются после загрузки org-scoped `connection`.
  const connectionQueryKey = useOrgScopedQueryKey(CI_LOG_CONNECTION_KEY)!;

  // `null` — черновик ещё не правили; поле показывает сохранённое.
  const [providerDraft, setProviderDraft] = useState<string | null>(null);
  const [hostDraft, setHostDraft] = useState<string | null>(null);
  const [pat, setPat] = useState("");
  const [urlError, setUrlError] = useState<string | null>(null);
  const [patError, setPatError] = useState<string | null>(null);
  const [verifyResult, setVerifyResult] = useState<CiLogVerifyResult | null>(
    null,
  );
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [disconnectOpen, setDisconnectOpen] = useState(false);

  const verify = useCiLogVerify({
    onAccepted: (data) => {
      setVerifyResult(data);
      setSubmitError(null);
    },
    onRejected: (err) => {
      setVerifyResult(null);
      setSubmitError(describeCiLogError(err, t));
    },
  });

  // Ключ фиксируется в `onMutate`: ответ пишется в кэш той организации,
  // из которой запрос ушёл.
  const putMutation = useMutation<
    CiLogConnection,
    Error,
    CiLogConnectionBody,
    readonly unknown[]
  >({
    mutationFn: (body) => putCiLogConnection(body),
    onMutate: () => connectionQueryKey,
    onSuccess: (data, _variables, key) => {
      queryClient.setQueryData(key, data);
      void queryClient.resetQueries({ queryKey: CI_LOG_STATUS_KEY });
      setPat("");
      setProviderDraft(null);
      setHostDraft(null);
      setSubmitError(null);
      setVerifyResult(null);
      setSuccessMessage(
        t("connections.ciLogs.connectSuccess", {
          login: data.principal_login,
        }),
      );
    },
    onError: (err) => {
      setSubmitError(describeCiLogError(err, t));
    },
  });

  const deleteMutation = useMutation<void, Error, void, readonly unknown[]>({
    mutationFn: () => deleteCiLogConnection(),
    onMutate: () => connectionQueryKey,
    onSuccess: (_data, _variables, key) => {
      queryClient.setQueryData(key, DISCONNECTED);
      queryClient.removeQueries({ queryKey: CI_LOG_STATUS_KEY });
      setDisconnectOpen(false);
      setSubmitError(null);
      setVerifyResult(null);
      setProviderDraft(null);
      setHostDraft(null);
      setPat("");
      setSuccessMessage(t("connections.ciLogs.disconnect.success"));
    },
    onError: (err) => {
      setDisconnectOpen(false);
      setSubmitError(describeCiLogError(err, t));
    },
  });

  if (providers.isPending || connection.isPending) {
    return <Loader />;
  }

  if (providers.isError || connection.isError) {
    return (
      <Stack gap="md">
        <Title order={2}>{t("connections.ciLogs.title")}</Title>
        <Alert color="red" title={t("connections.errorHeading")}>
          {t("connections.loadingError")}
        </Alert>
      </Stack>
    );
  }

  const providerList = providers.data;
  const connectionData = connection.data;

  // Сохранённого провайдера может не быть в каталоге — тогда первый.
  const storedProviderAvailable = providerList.some(
    (p) => p.name === connectionData.provider,
  );
  const defaultProvider = storedProviderAvailable
    ? connectionData.provider!
    : (providerList[0]?.name ?? "");
  const providerValue = providerDraft ?? defaultProvider;
  const hostValue = hostDraft ?? connectionData.host_url ?? "";

  const repoHostUrl = forgejoStatus.data?.host_url ?? null;
  const showHostHint =
    forgejoStatus.data?.connected === true &&
    repoHostUrl !== null &&
    hostValue.trim() === "";

  const validate = (): boolean => {
    const hostEmpty = hostValue.trim() === "";
    const patEmpty = pat.trim() === "";
    setUrlError(hostEmpty ? t("connections.ciLogs.hostRequired") : null);
    setPatError(patEmpty ? t("connections.ciLogs.patRequired") : null);
    return !hostEmpty && !patEmpty;
  };

  const buildBody = (): CiLogConnectionBody => ({
    provider: providerValue,
    host_url: hostValue,
    credentials: { pat },
  });

  // «Проверить» и «Подключить» сбрасывают сообщения и валидируют одинаково.
  const submitWith =
    (mutate: (body: CiLogConnectionBody) => void) => (): void => {
      verify.markEdited();
      setSubmitError(null);
      setVerifyResult(null);
      setSuccessMessage(null);
      if (validate()) {
        mutate(buildBody());
      }
    };
  const handleVerifyClick = submitWith(verify.mutate);
  const handleConnectClick = submitWith(putMutation.mutate);

  return (
    <Stack gap="md">
      <Group justify="space-between" align="center">
        <Title order={2}>{t("connections.ciLogs.title")}</Title>
        {isConnected ? (
          <Button
            color="red"
            variant="subtle"
            onClick={() => {
              setSubmitError(null);
              setSuccessMessage(null);
              verify.markEdited();
              setDisconnectOpen(true);
            }}
            loading={deleteMutation.isPending}
          >
            {t("connections.ciLogs.disconnectButton")}
          </Button>
        ) : null}
      </Group>

      <Text c="dimmed">{t("connections.ciLogs.intro")}</Text>

      <CiLogSummary connection={connectionData} />

      {isConnected ? (
        <CiLogLiveStatus
          query={status}
          storedLogin={connectionData.principal_login}
          onRecheck={() => {
            void queryClient.invalidateQueries({
              queryKey: CI_LOG_STATUS_KEY,
            });
          }}
        />
      ) : null}

      <CiLogFormFields
        providers={providerList.map((p) => p.name)}
        provider={providerValue}
        host={hostValue}
        pat={pat}
        urlError={urlError}
        patError={patError}
        repoHostUrl={showHostHint ? repoHostUrl : null}
        onProviderChange={(value) => {
          setProviderDraft(value);
          verify.markEdited();
          setVerifyResult(null);
        }}
        onHostChange={(value) => {
          setHostDraft(value);
          verify.markEdited();
          setUrlError(null);
          setVerifyResult(null);
        }}
        onPatChange={(value) => {
          setPat(value);
          verify.markEdited();
          setPatError(null);
          setVerifyResult(null);
        }}
      />

      <CiLogMessages
        verifiedLogin={verifyResult?.principal_login ?? null}
        successMessage={successMessage}
        errorMessage={submitError}
      />

      <Group>
        <Button
          variant="default"
          onClick={handleVerifyClick}
          loading={verify.isPending}
        >
          {t("connections.ciLogs.verifyButton")}
        </Button>
        <Button onClick={handleConnectClick} loading={putMutation.isPending}>
          {t("connections.ciLogs.connectButton")}
        </Button>
      </Group>

      <CiLogDisconnectModal
        opened={disconnectOpen}
        isSubmitting={deleteMutation.isPending}
        onConfirm={() => {
          deleteMutation.mutate();
        }}
        onCancel={() => {
          setDisconnectOpen(false);
        }}
      />
    </Stack>
  );
}
